import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Mic, Square, Upload, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { InlineUpgradePrompt } from '../UpgradePrompt';
import type { UpgradeContext } from '../UpgradePrompt';

export type PendingWorkspaceAudio =
  | null
  | { source: 'file'; file: File }
  | { source: 'mic'; blob: Blob; filename: string };

const UPGRADE_CONTEXT: UpgradeContext = 'feature_access';

function pickRecorderMimeType(): string | undefined {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
  ];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return undefined;
  }
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) {
      return c;
    }
  }
  return undefined;
}

function extensionForMime(mime: string | undefined): string {
  if (!mime) {
    return 'webm';
  }
  if (mime.includes('mp4') || mime.includes('aac')) {
    return 'm4a';
  }
  return 'webm';
}

export interface MemoryWorkspaceAudioAttachProps {
  canUploadAudio: boolean;
  disabled: boolean;
  currentTier: string;
  nextTier: string;
  pending: PendingWorkspaceAudio;
  onPendingChange: (next: PendingWorkspaceAudio) => void;
  onNavigateUpgrade: () => void;
}

export function MemoryWorkspaceAudioAttach({
  canUploadAudio,
  disabled,
  currentTier,
  nextTier,
  pending,
  onPendingChange,
  onNavigateUpgrade,
}: MemoryWorkspaceAudioAttachProps) {
  const { t } = useTranslation('memories');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [recordError, setRecordError] = useState<string | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
  }, []);

  const clearTimer = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimer();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          /* ignore */
        }
      }
      stopStream();
    };
  }, [clearTimer, stopStream]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) {
      return;
    }
    setRecordError(null);
    onPendingChange({ source: 'file', file: f });
  };

  const clearPending = () => {
    onPendingChange(null);
    setRecordError(null);
  };

  const startRecording = async () => {
    setRecordError(null);
    if (typeof MediaRecorder === 'undefined') {
      setRecordError(t('audio.recordingNotSupported') as string);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = pickRecorderMimeType();
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;

      rec.ondataavailable = (ev) => {
        if (ev.data.size > 0) {
          chunksRef.current.push(ev.data);
        }
      };

      rec.onstop = () => {
        stopStream();
        const blob = new Blob(chunksRef.current, {
          type: rec.mimeType || mimeType || 'audio/webm',
        });
        const ext = extensionForMime(rec.mimeType || mimeType);
        onPendingChange({
          source: 'mic',
          blob,
          filename: `recording.${ext}`,
        });
        chunksRef.current = [];
        mediaRecorderRef.current = null;
        setIsRecording(false);
        clearTimer();
        setRecordSeconds(0);
      };

      rec.start(200);
      setIsRecording(true);
      setRecordSeconds(0);
      tickRef.current = setInterval(() => {
        setRecordSeconds((s) => s + 1);
      }, 1000);
    } catch {
      setRecordError(t('audio.microphonePermissionDenied') as string);
      stopStream();
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      rec.stop();
    } else {
      setIsRecording(false);
      clearTimer();
      stopStream();
    }
  };

  const pendingLabel =
    pending?.source === 'file'
      ? pending.file.name
      : pending?.source === 'mic'
        ? pending.filename
        : null;

  if (!canUploadAudio) {
    return (
      <InlineUpgradePrompt
        context={UPGRADE_CONTEXT}
        currentTier={currentTier}
        nextTier={nextTier}
        onUpgrade={onNavigateUpgrade}
        className="border-workspace-border bg-workspace-surface-tertiary/50"
      />
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-workspace-border bg-workspace-surface-tertiary/40 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.wav,.webm,.ogg,.flac,.aac"
          className="sr-only"
          aria-label={t('audio.chooseFileAria') as string}
          onChange={handleFileChange}
          disabled={disabled || isRecording}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || isRecording}
          className="border-workspace-border"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          {t('audio.chooseFile') as string}
        </Button>

        {!isRecording ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            className="border-workspace-border"
            onClick={() => void startRecording()}
          >
            <Mic className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            {t('audio.startRecording') as string}
          </Button>
        ) : (
          <Button
            type="button"
            variant="destructive"
            size="sm"
            disabled={disabled}
            onClick={stopRecording}
          >
            <Square className="mr-2 h-3 w-3 shrink-0" aria-hidden />
            {t('audio.stopRecording', { seconds: recordSeconds }) as string}
          </Button>
        )}

        {pending && !isRecording && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={clearPending}
            className="text-workspace-text-secondary"
          >
            <X className="mr-1 h-4 w-4 shrink-0" aria-hidden />
            {t('audio.clearAttachment') as string}
          </Button>
        )}
      </div>

      {pendingLabel && !isRecording && (
        <p className="text-xs text-workspace-text-secondary">
          {t('audio.attachedLabel', { name: pendingLabel }) as string}
        </p>
      )}

      {recordError && (
        <p className="text-xs text-red-500" role="alert">
          {recordError}
        </p>
      )}

      <p className="text-xs text-workspace-text-tertiary">
        {t('audio.hintTranscription') as string}
      </p>
    </div>
  );
}
